﻿import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@pms/shared';
import { CalendarX, CheckSquare, Clock, Users } from 'lucide-react';
import { usePermission } from '@/hooks/usePermission';
import { StatCard } from '@/components/ui/stat-card';
import { usersService } from '@/services/users.service';
import { attendanceService } from '@/services/attendance.service';
import { leaveService } from '@/services/leave.service';
import { tasksService } from '@/services/tasks.service';

const todayISO = new Date().toISOString().split('T')[0]!;

function KpiSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-[100px] animate-pulse rounded-xl bg-muted/50" />
      ))}
    </div>
  );
}

export function ManagerKPIs() {
  const { has } = usePermission();

  const staffQ = useQuery({
    queryKey: ['dashboard', 'manager-kpi', 'staff-count'],
    queryFn: () => usersService.list({ page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.STAFF_VIEW_TEAM),
  });

  const attendanceQ = useQuery({
    queryKey: ['dashboard', 'manager-kpi', 'attendance-summary', todayISO],
    queryFn: () => attendanceService.getDailySummary(todayISO),
    enabled: has(PERMISSIONS.ATTENDANCE_VIEW_TEAM) || has(PERMISSIONS.ATTENDANCE_VIEW_ALL),
  });

  const pendingLeaveQ = useQuery({
    queryKey: ['dashboard', 'manager-kpi', 'pending-leave'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.LEAVE_APPROVE),
  });

  const assignedByMeQ = useQuery({
    queryKey: ['dashboard', 'manager-kpi', 'assigned-by-me'],
    queryFn: () => tasksService.assignedByMe(),
    enabled: has(PERMISSIONS.TASK_VIEW_TEAM),
  });

  const isLoading =
    staffQ.isLoading || attendanceQ.isLoading || pendingLeaveQ.isLoading || assignedByMeQ.isLoading;

  if (isLoading) return <KpiSkeleton />;

  const teamSize = staffQ.data?.pagination?.total ?? '—';
  const presentToday = attendanceQ.data
    ? (attendanceQ.data.present ?? 0) + (attendanceQ.data.late ?? 0)
    : '—';
  const leavePending = pendingLeaveQ.data?.pagination?.total ?? '—';

  const tasks = assignedByMeQ.data ?? [];
  const dueToday = tasks.filter((t) => t.periodic_end_date === todayISO).length;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Team Size"
        value={teamSize}
        hint="Total team members"
        icon={Users}
        tone="teal"
      />
      <StatCard
        label="Present Today"
        value={presentToday}
        hint="Present + late arrivals today"
        icon={Clock}
        tone="emerald"
      />
      <StatCard
        label="Leave Pending"
        value={leavePending}
        hint="Awaiting your approval"
        icon={CalendarX}
        tone="amber"
      />
      <StatCard
        label="Tasks Due Today"
        value={dueToday}
        hint="From tasks you assigned"
        icon={CheckSquare}
        tone="violet"
      />
    </div>
  );
}
